import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm, FieldValues } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@shared/ui/button';
import {
  companyAddEditValidators,
  orderConfirmAddresSchema,
} from '../lib/validators';
import { LoadingWrapper } from '@shared/ui/loading-wrapper';
import { InputField } from '@shared/ui/input-field';
import { formatPhoneNumber } from '@shared/lib/validators/phone-normalize';
import {
  companiesLegalEntityAddEditSections,
  orderConfirmFormSections,
} from '../lib/form-sections';
import { CustomModal } from '@shared/ui/custom-modal';
import { ConfirmModal } from '@shared/ui/confirm-modal';
import { useAppDispatch, useAppSelector } from '../../../app/store/store';
import { selectUser } from '../../../entities/auth/model';
import {
  createLegalEntity,
  deleteLegalEntity,
  fetchLegalEntityById,
  partialUpdateLegalEntity,
  updateLegalEntity,
} from '../../../entities/legal-entity/model';
import { AbilityContext } from '@shared/lib/ability';
import { addCompanyLegalEntities } from '../../../entities/auth/model';
import {
  deleteAppLegalEntity,
  resetCurrentLegalEntity,
  selectCurrentLegalEntity,
  updateAppLegalEntity,
} from '../../../entities/legal-entity/model';
import { AvatarAddEditForm } from '@shared/ui/avatar-add-edit-form';
import { Address } from '../../../types/interfaces';
import { setErrorNotification } from '../../../app/store/notifications';
import { FormCard } from '@shared/ui/form-card';
import { getTaxLabel } from '@shared/lib/tax-label';

export const CompaniesCompanyAddEdit = () => {
  const ability = useContext(AbilityContext);
  const status = useAppSelector((state) => state.legalEntities.status);
  const { legalEntityId, companyId } = useParams();
  const legalEntity = useAppSelector(selectCurrentLegalEntity);
  const currentCompany = useAppSelector(
    (state) => state.companies.currentCompany
  );
  const me = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();


  const resolvedCompanyId: number | undefined =
    (companyId ? Number(companyId) : undefined) ||
    currentCompany?.id ||
    me?.company?.id;

  
  const STAFF_OR_MANAGER_GROUPS = [
    'MANAGER',
    'OFFICE_MANAGER',
    'SALES_DIRECTOR',
    'admins',
    'HD_HEAD',
    'HD_ENGINEER',
    'HD_SERVICE_ENGINEER',
  ];
  const isInternalStaff =
    me?.is_staff ||
    me?.is_superuser ||
    (me?.groups ?? []).some((g: string) =>
      STAFF_OR_MANAGER_GROUPS.includes(g)
    );
  const isForeignCompany =
    !isInternalStaff &&
    Boolean(resolvedCompanyId) &&
    Boolean(me?.company?.id) &&
    resolvedCompanyId !== me?.company?.id;
  // При создании первого юр. лица в компании поле «Страна» по
  // умолчанию подставляется из существующих юр. лиц этой же
  // компании. Так юзеру не приходится каждый раз выбирать вручную
  // (а на практике 99% случаев — все юр. лица одной компании в
  // одной стране). Если у компании ещё нет юр. лиц или ни у одного
  // из них страна не задана — фолбэк на 'ru'.
  // На редактировании сохраняем собственную страну юр. лица: её
  // нельзя «сбрасывать» только потому, что у компании-родителя
  // первое юр. лицо в другой стране.
  const inheritedCountry =
    currentCompany?.legal_entities?.find((le) => le.country)?.country || 'ru';

  const buildDefaults = (le: typeof legalEntity) => ({
    ...le,
    country: le?.country || inheritedCountry,
    phoneNumber: le.phones?.length ? le.phones[0] : '',
    additionalPhone: le.phones?.length >= 2 ? le.phones[1] : '',
    legalAddress: le.addresses?.length ? le.addresses[0] : {},
  });

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(companyAddEditValidators),
    shouldUnregister: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    defaultValues: buildDefaults(legalEntity) as any,
  });
  useEffect(() => {
    if (status === 'idle' && legalEntityId) {
      dispatch(fetchLegalEntityById(Number(legalEntityId)));
    }
  }, [status, legalEntityId, dispatch]);
  useEffect(() => {
    // inheritedCountry в дефолтах — реактивен через зависимость
    // currentCompany ниже: если страница «добавление юр. лица»
    // открылась до того, как currentCompany загрузился, мы
    // переинициализируем форму, как только подтянем компанию.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reset(buildDefaults(legalEntity) as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalEntity, reset, inheritedCountry]);

  const onSubmit = async (body: FieldValues) => {
    if (body.additionalPhone) {
      body.phones = [
        formatPhoneNumber(body.phoneNumber),
        formatPhoneNumber(body.additionalPhone),
      ];
    } else {
      body.phones = [formatPhoneNumber(body.phoneNumber)];
    }

    // Жёсткий стоп до любого dispatch-а: чужой companyId в URL у
    // не-FMeter-юзера = попытка повлиять на чужую компанию. Сообщение
    // намеренно нейтральное, чтобы не подсказывать атакующему детали.
    if (isForeignCompany) {
      dispatch(
        setErrorNotification(
          'Нет прав на изменение этой компании.'
        )
      );
      return;
    }

    if (ability.can('add', 'legalentity')) {
      if (!legalEntityId) {
        if (!resolvedCompanyId) {
          dispatch(
            setErrorNotification(
              'Не удалось определить компанию для юр. лица. Откройте карточку компании и добавьте юр. лицо оттуда.'
            )
          );
          return;
        }
        dispatch(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          createLegalEntity({
            ...body,
            id: legalEntityId,
            company: resolvedCompanyId,
            addresses: [{ ...body.legalAddress, type: 'legal' }],
          } as any)
        ).then((data) => {
          dispatch(addCompanyLegalEntities(data.payload));
        });
      }
    }
    if (ability.can('change', 'legalentity')) {
      if (legalEntityId) {
        const currentAddresses: Address[] = legalEntity.addresses ?? [];
        const editedLegal: Address = {
          ...(currentAddresses[0] ?? {}),
          ...body.legalAddress,
          type: 'legal' as const,
        };
        const updatedAddresses: Address[] =
          currentAddresses.length > 0
            ? [editedLegal, ...currentAddresses.slice(1)]
            : [editedLegal];

        dispatch(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          updateLegalEntity({
            ...body,
            id: Number(legalEntityId),
            addresses: updatedAddresses,
          } as any)
        ).then(() => {
          dispatch(resetCurrentLegalEntity());
        });
      }
    }
    navigate(-1);
  };

  const addDeliveryAddress = (address: Address) => {
    const currentAddresses: Address[] = legalEntity.addresses ?? [];
    const newAddresses = [
      ...currentAddresses,
      {
        ...address,
        // Адрес доставки = type='physical'. Раньше тут стоял
        // 'legal' — из-за этого все адреса (и юр., и доставки)
        // были одного типа, и фильтр в правой колонке
        // (`type === 'legal'`) показывал ВСЁ — поэтому юзеру
        // казалось, что юр. адрес «появляется» в списке доставок.
        type: 'physical' as const,
      },
    ];
    dispatch(
      partialUpdateLegalEntity({
        id: legalEntity.id,
        addresses: newAddresses,
      })
    );
    dispatch(
      updateAppLegalEntity({
        ...legalEntity,
        addresses: newAddresses,
      })
    );
    // Раньше тут стоял `setValue('legalAddress', address)` — это и
    // приводило к тому, что добавление/редактирование адреса доставки
    // подменяло поля юр. адреса в форме, а следующий submit писал
    // их обратно в БД. Никаких setValue: добавляем доставочный
    // адрес и точка.
  };

  const editAddress = (oldAddress: Address, newAddress: Address) => {
    const currentAddresses: Address[] = legalEntity.addresses ?? [];
    const oldKey = JSON.stringify(oldAddress);
    // type='physical' по той же причине, что и в addDeliveryAddress.
    const updated: Address = { ...newAddress, type: 'physical' as const };
    const newAddresses = currentAddresses.map((a) =>
      JSON.stringify(a) === oldKey ? updated : a
    );
    dispatch(
      partialUpdateLegalEntity({
        id: legalEntity.id,
        addresses: newAddresses,
      })
    );
    dispatch(
      updateAppLegalEntity({
        ...legalEntity,
        addresses: newAddresses,
      })
    );
    // setValue('legalAddress', updated) удалён — см. addDeliveryAddress.
  };

  const removeAddress = (deletingAddress: Address) => {
    const currentAddresses: Address[] = legalEntity.addresses ?? [];
    const newAddresses = currentAddresses.filter(
      (address: Address) => JSON.stringify(address) !== JSON.stringify(deletingAddress)
    );
    dispatch(
      partialUpdateLegalEntity({
        id: legalEntity.id,
        addresses: newAddresses,
      })
    );
    dispatch(
      updateAppLegalEntity({
        ...legalEntity,
        addresses: newAddresses,
      })
    );
  };

  const onUpdateAvatar = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formData = new FormData();
    formData.append('photo', e.target.files?.[0] ?? '');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch(partialUpdateLegalEntity({ id: legalEntity.id, data: formData } as any));
  };
  const onDeleteAvatar = () => {
    const formData = new FormData();
    formData.append('clear_photo', 'true');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch(partialUpdateLegalEntity({ id: legalEntity.id, data: formData } as any));
  };

  // Группируем секции для аккуратного рендеринга в отдельных FormCard:
  //   companyDataSection — реквизиты компании
  //   legalAddressSection — юр. адрес
  //   (адреса доставки в отдельной карточке справа)
  const sections = companiesLegalEntityAddEditSections as any[];
  const rawCompanyDataSection = sections.find((s) => s.title === 'Данные компании');
  const legalAddressSection = sections.find((s) => s.title === 'Юридический адрес');

  // Лейбл налогового номера зависит от страны юр.лица. На этом
  // экране страна меняется в реальном времени (есть селект «Страна»
  // выше), поэтому подсматриваем актуальное значение через `watch`,
  // а инициалом служит `legalEntity.country` из стора. Лейбл инпута
  // `inn` подменяем на лету — сам form-sections.js статичен и общий
  // на проект, его не трогаем.
  const watchedCountry = watch('country' as any);
  const taxLabel = getTaxLabel(watchedCountry || legalEntity?.country);
  const companyDataSection = rawCompanyDataSection
    ? {
        ...rawCompanyDataSection,
        fields: (rawCompanyDataSection.fields as any[]).map((f) =>
          f.id === 'inn' ? { ...f, label: taxLabel } : f
        ),
      }
    : null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderField = (data: any, i: number) => {
    if (data.id === 'phoneNumber' || data.id === 'additionalPhone') {
      return (
        <Controller
          key={data.id || i}
          name={data.inputName}
          control={control}
          render={({ field }) => (
            <InputField
              {...field}
              as={data.as}
              mask={data.mask}
              id={data.id}
              inputName={data.inputName}
              label={data.label}
              errors={errors}
              register={register}
            />
          )}
        />
      );
    }
    return (
      <InputField
        key={data.id || i}
        id={data.id}
        inputName={data.inputName}
        label={data.label}
        as={data.as}
        mask={data.mask}
        errors={errors}
        register={register}
      />
    );
  };

  // Не-FMeter-юзер залез на /company/{чужая}/legal-entities — рисуем
  // вежливую заглушку вместо формы. Сабмит уже защищён выше, но без
  // этого UX был бы странный: рендерится форма, в которой нельзя
  // ничего сохранить.
  if (isForeignCompany) {
    return (
      <div className="flex flex-col items-center justify-center px-5 py-[60px] text-center">
        <h3 className="text-2xl font-semibold text-[#1a202c] mb-3">
          Нет доступа
        </h3>
        <p className="text-base text-[#718096] mb-8 max-w-[500px]">
          У вас нет прав изменять данные этой компании. Откройте карточку
          собственной компании и добавляйте юр. лица оттуда.
        </p>
        <Button onClick={() => navigate(`/company/${me?.company?.id ?? ''}`)}>
          К моей компании
        </Button>
      </div>
    );
  }

  return (
    <LoadingWrapper type={'main'} status={status}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* ── Левая колонка (xl:col-span-2): данные + юр. адрес ── */}
          <div className="xl:col-span-2 flex flex-col gap-6">
            {companyDataSection ? (
              <FormCard
                title="Данные компании"
                description={`Название, ${taxLabel}, контакты и реквизиты юр. лица`}
              >
                <div className="space-y-1">
                  {/* Страна — отдельным полем перед остальными */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                    <div className="my-4 pb-[10px]">
                      <label className="mb-2 font-montserrat text-[#2d2c2c] text-[16px] font-medium block">
                        Страна
                      </label>
                      <select
                        {...register('country')}
                        className="font-montserrat w-full px-3 h-[40px] border border-[#D0D3D8] rounded-[10px] text-[14px] font-normal transition-all duration-200 focus:outline-none focus:border-[#007aff] focus:shadow-[0_0_0_4px_rgba(0,122,255,0.21)] hover:border-[#007aff]"
                      >
                        <option value="ru">Россия</option>
                        <option value="kz">Казахстан</option>
                      </select>
                    </div>
                  </div>

                  {/* Поля секции «Данные компании» — раскладываем парами */}
                  {(() => {
                    const fields = companyDataSection.fields as any[];
                    const rows: any[][] = [];
                    for (let i = 0; i < fields.length; i += 2) {
                      rows.push(fields.slice(i, i + 2));
                    }
                    return rows.map((row, idx) => (
                      <div
                        key={idx}
                        className="grid grid-cols-1 md:grid-cols-2 gap-x-4"
                      >
                        {row.map((f, j) => renderField(f, j))}
                      </div>
                    ));
                  })()}

                  {companyDataSection.checkbox && (
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="checkbox"
                        title={companyDataSection.checkbox.label}
                        id={companyDataSection.checkbox.id}
                        className="w-[18px] h-[18px]"
                        style={{ accentColor: '#E3282A' }}
                        {...register(companyDataSection.checkbox.inputName)}
                      />
                      <label
                        htmlFor={companyDataSection.checkbox.id}
                        className="text-[#2d2c2c] text-sm font-medium"
                      >
                        {companyDataSection.checkbox.label}
                      </label>
                    </div>
                  )}
                </div>
              </FormCard>
            ) : null}

            {legalAddressSection ? (
              <FormCard
                title="Юридический адрес"
                description="Адрес регистрации компании по документам"
              >
                <div className="space-y-1">
                  {(() => {
                    const fields = legalAddressSection.fields as any[];
                    const rows: any[][] = [];
                    for (let i = 0; i < fields.length; i += 2) {
                      rows.push(fields.slice(i, i + 2));
                    }
                    return rows.map((row, idx) => (
                      <div
                        key={idx}
                        className="grid grid-cols-1 md:grid-cols-2 gap-x-4"
                      >
                        {row.map((f, j) => renderField(f, j))}
                      </div>
                    ));
                  })()}
                </div>
              </FormCard>
            ) : null}

            {/* Кнопки сохранения/удаления */}
            <div className="flex flex-wrap gap-2">
              <Button type="submit">
                {legalEntityId ? 'Сохранить' : 'Добавить'}
              </Button>
              <Button isWhite onClick={() => navigate(-1)}>
                Отмена
              </Button>
              {legalEntityId ? (
                <Button
                  variant="danger"
                  className="ml-auto"
                  onClick={() => {
                    dispatch(deleteLegalEntity(Number(legalEntityId)));
                    dispatch(deleteAppLegalEntity(legalEntityId));
                    navigate(-1);
                  }}
                >
                  Удалить юр. лицо
                </Button>
              ) : null}
            </div>
          </div>

          {/* ── Правая колонка: адреса доставки ── */}
          <div className="xl:col-span-1">
            <FormCard
              title="Адреса доставки"
              description="Куда отправлять заказы и документы. Можно несколько."
            >
              {/* Список адресов доставки. Раньше фильтр стоял
                  `type === 'legal'`, что плюс к багам в add/edit
                  показывал тут ВСЁ (включая сам юр. адрес).
                  Теперь — `slice(1)`: первый адрес всегда
                  считается юр. адресом (с этим работает onSubmit
                  страницы), остальные — доставочные. Это
                  совместимо и со старыми записями (которые могли
                  быть с любыми типами), и с новыми. */}
              <DeliveryAddressesSection
                addresses={(legalEntity.addresses ?? []).slice(1)}
                onRemove={removeAddress}
                onEdit={editAddress}
              />
              <div className="mt-3">
                <AddDeliveryAddressModal handler={addDeliveryAddress} />
              </div>
            </FormCard>
          </div>
        </div>
      </form>
    </LoadingWrapper>
  );
};

export default CompaniesCompanyAddEdit;

interface DeliveryAddressesSectionProps {
  addresses: Address[];
  onRemove: (address: Address) => void;
  onEdit: (oldAddress: Address, newAddress: Address) => void;
}

export function DeliveryAddressesSection({ addresses, onRemove, onEdit }: DeliveryAddressesSectionProps) {
  if (!addresses || addresses.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-2">Адресов доставки пока нет</p>
    );
  }

  const formatAddress = (a: Address) =>
    [a.country, a.region, a.city, a.street, a.house, a.postcode]
      .filter(Boolean)
      .join(', ');

  return (
    <div className="flex flex-col gap-2">
      {addresses.map((data, i) => (
        // Раньше тут стоял `onClick={() => onChange(data)}` — клик
        // по строке адреса доставки вызывал
        // `setValue('legalAddress', data)`, и юр. адрес в форме
        // молча подменялся выбранным доставочным. Это и был один
        // из источников «редактирую один — меняется другой».
        // У строки есть отдельные кнопки «Редактировать»/«Удалить» —
        // больше клик по строке ничего не делает.
        <div
          key={i}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2"
        >
          <p
            className="text-sm text-[#2d2c2c] truncate"
            title={formatAddress(data)}
          >
            {formatAddress(data)}
          </p>
          <div className="flex items-center gap-3 mt-1">
            {/*
              Раньше «Редактировать» дёргал onChange — это просто
              затирало `legalAddress` в форме юр. лица и НИКАКОГО
              окна не открывало (тестировщик жаловался: «форма не
              открывается»). Теперь — отдельная модалка, заполненная
              текущим адресом, через onEdit отправляет partial-update.
            */}
            <EditDeliveryAddressModal
              address={data}
              onSubmit={(updated) => onEdit(data, updated)}
            />
            <ConfirmModal
              openButton={
                <button
                  type="button"
                  className="text-xs font-medium text-gray-500 hover:text-[#e3282a]"
                >
                  Удалить
                </button>
              }
              confirm={() => onRemove(data)}
              texts={{
                title: 'Удаление',
                text: 'Удалить адрес доставки?',
                cancel: 'Отмена',
                confirm: 'Удалить',
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

interface EditDeliveryAddressModalProps {
  address: Address;
  onSubmit: (address: Address) => void;
}

export function EditDeliveryAddressModal({ address, onSubmit }: EditDeliveryAddressModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(orderConfirmAddresSchema),
    shouldUnregister: false,
    defaultValues: {
      country: address.country ?? '',
      region: address.region ?? '',
      city: address.city ?? '',
      street: address.street ?? '',
      house: address.house ?? '',
      apartment: address.apartment ?? '',
      postcode: address.postcode ?? '',
    },
  });
  const [isModalClosed, setModalClosed] = useState(false);

  // Если родитель пересохранил юр. лицо и address пришёл свежий —
  // синхронизируем форму, чтобы при следующем открытии не было
  // протухших значений.
  useEffect(() => {
    reset({
      country: address.country ?? '',
      region: address.region ?? '',
      city: address.city ?? '',
      street: address.street ?? '',
      house: address.house ?? '',
      apartment: address.apartment ?? '',
      postcode: address.postcode ?? '',
    });
  }, [address, reset]);

  const submitEdit = (data: FieldValues) => {
    onSubmit(data as Address);
    // Закрываем модалку через `isClosed`, и сразу же сбрасываем
    // флаг, чтобы следующее открытие отработало штатно.
    setModalClosed(true);
    setTimeout(() => setModalClosed(false), 0);
  };

  return (
    <CustomModal
      isClosed={isModalClosed}
      open={() => setModalClosed(false)}
      openButton={
        <button
          type="button"
          className="text-xs font-medium text-[#e3282a] hover:text-[#c82022]"
        >
          Редактировать
        </button>
      }
    >
      <div className="min-w-[420px]">
        {/*
          ВАЖНО: НЕ оборачиваем поля в <form>. Эта модалка рендерится
          внутри outer-<form> страницы юр. лица, а вложенные <form>
          невалидны — submit-event всплывает до внешней формы, она
          обрабатывает свой `onSubmit` и добавляет ОТРЕДАКТИРОВАННЫЙ
          адрес как НОВЫЙ (через `hasAddress=false` ветку). Поэтому
          здесь обычный <div>, а сабмит дёргаем onClick'ом на кнопке —
          она `type="button"`, никакого нативного submit-события не
          порождает, к outer-form вообще не подходит.
        */}
        <div className="space-y-1">
          <h3 className="text-base font-semibold text-[#2d2c2c] mb-3">
            Редактирование адреса доставки
          </h3>
          {(() => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const fields = orderConfirmFormSections.fields as any[];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const rows: any[][] = [];
            for (let i = 0; i < fields.length; i += 2) {
              rows.push(fields.slice(i, i + 2));
            }
            return rows.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-1 md:grid-cols-2 gap-x-4"
              >
                {row.map((field) => (
                  <InputField
                    key={field.id}
                    id={field.id}
                    inputName={field.inputName}
                    label={field.label}
                    as={field.as}
                    mask={field.mask}
                    errors={errors}
                    register={register}
                  />
                ))}
              </div>
            ));
          })()}
          <div className="pt-3">
            <Button
              type="button"
              onClick={(e) => {
                // На всякий случай гасим всплытие — react-hook-form
                // handleSubmit сам preventDefault не вызывает на
                // click-событии (только на submit), а вот всплытия
                // мы хотим избежать в любом раскладе.
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(submitEdit)();
              }}
            >
              Сохранить
            </Button>
          </div>
        </div>
      </div>
    </CustomModal>
  );
}

interface AddDeliveryAddressModalProps {
  handler: (address: Address) => void;
}

export function AddDeliveryAddressModal({ handler }: AddDeliveryAddressModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(orderConfirmAddresSchema),
    shouldUnregister: false,
    defaultValues: {
      country: '',
      region: '',
      city: '',
      street: '',
      house: '',
      apartment: '',
      postcode: '',
    },
  });
  const [isModalOpen, setModalOpen] = useState(false);

  const addDeliveryAddress = (data: FieldValues) => {
    setModalOpen(true);
    handler(data as Address);
    reset();
  };

  return (
    <CustomModal
      isClosed={isModalOpen}
      open={() => setModalOpen(false)}
      openButton={<Button isWhite={true}>+ Добавить адрес</Button>}
    >
      <div className="min-w-[420px]">
        {/*
          ВАЖНО: НЕ оборачиваем поля в <form>. Эта модалка рендерится
          внутри outer-<form> страницы юр. лица, а вложенные <form>
          невалидны — submit-event с inner-кнопки всплывал к outer-form,
          и она дополнительно делала updateLegalEntity + navigate(-1).
          Симптомы из бага:
            * после «Добавить адрес» страницу перекидывает в список
              юр. лиц (это outer-form'ный navigate(-1));
            * адрес дублируется 2-3 раза (один раз кладёт сам add,
              второй — outer-onSubmit с уже изменённым стором);
            * «работает через раз» — race зависит от того, какой
              из двух dispatch'ей прилетит первым.
          Тот же фикс уже сделан в EditDeliveryAddressModal — теперь
          приводим к нему же: <div> вместо <form> + onClick на
          type="button" вызывает handleSubmit вручную.
        */}
        <div className="space-y-1">
          <h3 className="text-base font-semibold text-[#2d2c2c] mb-3">
            {orderConfirmFormSections.title}
          </h3>
          {/* Раскладываем поля адреса парами для аккуратности */}
          {(() => {
            const fields = orderConfirmFormSections.fields as any[];
            const rows: any[][] = [];
            for (let i = 0; i < fields.length; i += 2) {
              rows.push(fields.slice(i, i + 2));
            }
            return rows.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-1 md:grid-cols-2 gap-x-4"
              >
                {row.map((field) => (
                  <InputField
                    key={field.id}
                    id={field.id}
                    inputName={field.inputName}
                    label={field.label}
                    as={field.as}
                    mask={field.mask}
                    errors={errors}
                    register={register}
                  />
                ))}
              </div>
            ));
          })()}
          <div className="pt-3">
            <Button
              type="button"
              onClick={(e) => {
                // Дополнительно гасим всплытие — на всякий случай
                // (handleSubmit в react-hook-form preventDefault'ит
                // только submit-event, не click).
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(addDeliveryAddress)();
              }}
            >
              Добавить
            </Button>
          </div>
        </div>
      </div>
    </CustomModal>
  );
}
